import { useMemo } from 'react'
import useCharterDateRangesByMonth from 'hooks/CharterFlights/useCharterDateRangesByMonth'
import type { FlightsClassTypes } from 'constants/flight'
import type { CalendarItemBundleData } from './types'
import { getCheapestCalendarBundleData } from '../utils/bundlePricingDisplay'
import { useMaldivesDirectCalendarPricing } from './useMaldivesDirectCalendarPricing'

interface UseCheapestBundlePriceParams {
  checkIn?: string
  nights: number
  rooms: Array<App.Occupants>
  fareClass: FlightsClassTypes
  charterBundleRoute?: App.CharterBundleRoute
  useMemberPrice: boolean
  disabled?: boolean
}

/**
 * Fetches all charter bundle offers via a non-stream (light) API request,
 * then uses calendar + flight data to compute the lowest bundle price
 * for a specific check-in date across all offers.
 *
 * Unlike useCalendarItemData (stream-based), this hook:
 * - Uses isStream: false to get all offers in one request
 * - Uses calendar API for date-specific pricing (not base package price)
 * - Computes prices via the same useMaldivesOffersPricing logic as the landing page
 */
export function useCheapestBundlePrice(params: UseCheapestBundlePriceParams): CalendarItemBundleData {
  const {
    checkIn,
    nights,
    rooms,
    fareClass,
    charterBundleRoute,
    useMemberPrice,
    disabled = false,
  } = params

  const { dateRangesByMonth } = useCharterDateRangesByMonth({
    charterBundleRoute,
    occupancies: rooms,
    fareClass,
    disabled: disabled || !charterBundleRoute,
  })

  const { calendarPricingByDate, isLoading } = useMaldivesDirectCalendarPricing({
    rooms,
    fareClass,
    nights,
    charterBundleRoute,
    dateRangesByMonth,
    disabled,
  })

  const bundleData = useMemo<CalendarItemBundleData>(() => {
    if (disabled) {
      return { fetching: false, available: false, totalPrice: 0, pricePerPerson: 0, offerCount: 0 }
    }

    const calendarEntry = checkIn
      ? calendarPricingByDate[checkIn]
      : getCheapestCalendarBundleData(calendarPricingByDate, { useMemberPrice, priceMode: 'total' })

    if (!calendarEntry) {
      return { fetching: isLoading, available: false, totalPrice: 0, pricePerPerson: 0, offerCount: 0 }
    }

    return calendarEntry
  }, [disabled, checkIn, isLoading, calendarPricingByDate, useMemberPrice])

  return bundleData
}
